import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Avatar
} from '@mui/material';
import api from '../config/api';

const TelegramLinking = ({ onSuccess, onError }) => {
  const { t } = useTranslation();
  const [telegramId, setTelegramId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLinkTelegram = async (e) => {
    e.preventDefault();

    if (!telegramId) {
      setError(t('telegram.enterTelegramId'));
      return;
    }

    // Validate that the Telegram ID is a number
    const idNumber = parseInt(telegramId, 10);
    if (isNaN(idNumber)) {
      setError(t('telegram.invalidId'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/auth/telegram/link', {
        id: idNumber // Send as a number, not a string
      });

      setSuccess(t('telegram.linkSuccess'));
      setTelegramId('');

      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('telegram.linkFailed');
      setError(errorMessage);

      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar sx={{ bgcolor: '#0088cc', mr: 2 }}>T</Avatar>
        <Box>
          <Typography variant="body1" fontWeight="medium">
            Telegram
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('telegram.connectDescription')}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleLinkTelegram}>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          {t('telegram.getIdInstruction')}
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          label={t('telegram.telegramId')}
          value={telegramId}
          onChange={(e) => setTelegramId(e.target.value)}
          placeholder={t('telegram.idPlaceholder')}
          disabled={loading}
          type="number"
          size="small"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          required
          sx={{ mb: 2 }}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          fullWidth
          sx={{ bgcolor: '#0088cc', '&:hover': { bgcolor: '#0077b5' } }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('telegram.connectButton')}
        </Button>
      </Box>
    </Box>
  );
};

export default TelegramLinking;